import _ from 'lodash/fp'
import gql from 'graphql-tag'
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'

import { types as graphicsTypes, actions as graphicsActions } from '../reducers/graphics'
import { getCurrentClubId } from './network'
import { apolloClient } from '@fe/business/src/apollo'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * getAllGraphics () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query Graphics {
          graphics {
            themes {
              name
              parentName
              createdAt
            }
            themeAttributes {
              themeName
              elementName
              attributeName
              value
              createdAt
            }
            layers {
              name
              createdAt
            }
            elements {
              name
              shorthand
              groupName
              layerName
              geometryType
              createdAt
            }
            customAttributes {
              trackingGroupId
              attributeName
              value
              createdAt
            }
            attributes {
              name
              attributeUnitName
              createdAt
            }
            attributeUnits {
              name
              createdAt
            }
            attributeGraphics {
              elementName
              graphicsName
            }
            groups {
              name
              createdAt
            }
            geometryTypes
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.get('data.graphics', response)
  } catch (error) {
    return { error }
  }
}

export function * getGraphicsThemes () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query GraphicsThemes {
          graphicsThemes {
            name
            parentName
            createdAt
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsThemes', response)
  } catch (error) {
    return { error }
  }
}

export function * getGraphicsAttributes () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query GraphicsAttributes {
          graphicsAttributes {
            name
            attributeUnitName
            createdAt
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsAttributes', response)
  } catch (error) {
    return { error }
  }
}

export function * getGraphicsThemeAttributes () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query GraphicsThemeAttributes {
          graphicsThemeAttributes {
            themeName
            elementName
            attributeName
            value
            createdAt
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsThemeAttributes', response)
  } catch (error) {
    return { error }
  }
}

export function * getGraphicsAttributeUnits () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query GraphicsAttributeUnits {
          graphicsAttributeUnits {
            name
            createdAt
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsAttributeUnits', response)
  } catch (error) {
    return { error }
  }
}

export function * getGraphicsElements () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query GraphicsElements {
          graphicsElements {
            name
            shorthand
            groupName
            layerName
            geometryType
            createdAt
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsElements', response)
  } catch (error) {
    return { error }
  }
}

export function * getGraphicsGroups () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query GraphicsGroups {
          graphicsGroups {
            name
            createdAt
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsGroups', response)
  } catch (error) {
    return { error }
  }
}

export function * getGraphicsLayers () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query GraphicsLayers {
          graphicsLayers {
            name
            createdAt
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsLayers', response)
  } catch (error) {
    return { error }
  }
}

export function * getGraphicsAttributeGraphics () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: gql`
        query GraphicsAttributeGraphics {
          graphicsAttributeGraphics {
            elementName
            graphicsName
          }
        }
      `,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsAttributeGraphics', response)
  } catch (error) {
    return { error }
  }
}

export function * newElementEffect ({ body }) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: gql`
        mutation AddGraphicsElement($input: AddGraphicsElementInput!) {
          addGraphicsElement(input: $input) {
            rowsInserted
          }
        }
      `,
      variables: {
        input: body
      }
    })
    return { data: _.get('data.addGraphicsElement', response) }
  } catch (error) {
    return { error }
  }
}

export function * newAttributeEffect ({ body }) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: gql`
        mutation AddGraphicsAttribute($input: AddGraphicsAttributeInput!) {
          addGraphicsAttribute(input: $input) {
            rowsInserted
          }
        }
      `,
      variables: { input: body }
    })
    return _.get('data.addGraphicsAttribute', response)
  } catch (error) {
    return { error }
  }
}

const CLUB_GRAPHICS_CONTEXTS_QUERY = gql`
  query ClubGraphicsContexts($clubId: ID!) {
    clubGraphicsContexts(clubId: $clubId) {
      courseData {
        id
        elementName
        geometryType
        customAttributes
        coordinates {
          longitude
          latitude
        }
      }
      graphicsData {
        id
        elementName
        geometryType
        customAttributes
        coordinates {
          longitude
          latitude
        }
      }
    }
  }
`

export function * loadContextDataEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_GRAPHICS_CONTEXTS_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    const data = _.get('data.clubGraphicsContexts', response)
    // Consumers (see reducers/graphics.js contextDataByHoleId) read the top-level keys as
    // snake_case, matching the legacy REST shape; nested fields stay camelCase as-is.
    return {
      course_data: _.get('courseData', data),
      graphics_data: _.get('graphicsData', data)
    }
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

function * graphicsParser (graphics) {
  if (_.get('error', graphics)) {
    return yield put(graphicsActions.getGraphicsFailure())
  }
  const themes = _.flow(
    _.get('themes'),
    _.keyBy('name')
  )(graphics)
  if (!_.isEmpty(themes)) {
    yield put(graphicsActions.setThemes(themes))
  }

  const groups = _.flow(
    _.get('groups'),
    _.keyBy('name')
  )(graphics)
  if (!_.isEmpty(groups)) {
    yield put(graphicsActions.setGroups(groups))
  }

  const layers = _.flow(
    _.get('layers'),
    _.keyBy('name')
  )(graphics)
  if (!_.isEmpty(layers)) {
    yield put(graphicsActions.setLayers(layers))
  }

  const elements = _.flow(
    _.get('elements'),
    _.keyBy('name')
  )(graphics)
  if (!_.isEmpty(elements)) {
    yield put(graphicsActions.setElements(elements))
  }

  const attributes = _.flow(
    _.get('attributes'),
    _.keyBy('name')
  )(graphics)
  if (!_.isEmpty(attributes)) {
    yield put(graphicsActions.setAttributes(attributes))
  }

  const attributeUnits = _.flow(
    _.get('attributeUnits'),
    _.keyBy('name')
  )(graphics)
  if (!_.isEmpty(attributeUnits)) {
    yield put(graphicsActions.setAttributeUnits(attributeUnits))
  }

  const themeAttributes = _.flow(
    _.get('themeAttributes'),
    _.groupBy('themeName')
  )(graphics)
  if (!_.isEmpty(themeAttributes)) {
    yield put(graphicsActions.setThemeAttributes(themeAttributes))
  }

  const geometryTypes = _.flow(
    _.get('geometryTypes')
  )(graphics)
  if (!_.isEmpty(geometryTypes)) {
    yield put(graphicsActions.setGeometryTypes(geometryTypes))
  }

  const graphicsAttributes = _.flow(
    _.get('attributeGraphics'),
    _.groupBy('elementName')
  )(graphics)
  if (!_.isEmpty(graphicsAttributes)) {
    yield put(graphicsActions.setGraphicsAttributes(graphicsAttributes))
  }

  const graphicsMap = _.flow(
    _.get('graphics'),
    _.keyBy('name')
  )(graphics)
  if (!_.isEmpty(graphicsMap)) {
    yield put(graphicsActions.setGraphics(graphicsMap))
  }
}

function * loadGraphicsFlow () {
  const graphics = yield call(getAllGraphics)
  return yield call(graphicsParser, graphics)
}

function * loadGraphicsThemesFlow () {
  const graphics = yield call(getGraphicsThemes)
  return yield call(graphicsParser, { themes: graphics })
}

function * loadGraphicsAttributesFlow () {
  const graphics = yield call(getGraphicsAttributes)
  return yield call(graphicsParser, { attributes: graphics })
}

function * loadGraphicsThemeAttributesFlow () {
  const graphics = yield call(getGraphicsThemeAttributes)
  return yield call(graphicsParser, { themeAttributes: graphics })
}

function * loadGraphicsAttributeUnitsFlow () {
  const graphics = yield call(getGraphicsAttributeUnits)
  return yield call(graphicsParser, { attributeUnits: graphics })
}

function * loadGraphicsElementsFlow () {
  const graphics = yield call(getGraphicsElements)
  return yield call(graphicsParser, { elements: graphics })
}

function * loadGraphicsGroupsFlow () {
  const graphics = yield call(getGraphicsGroups)
  return yield call(graphicsParser, { groups: graphics })
}

function * loadGraphicsLayersFlow () {
  const layers = yield call(getGraphicsLayers)
  return yield call(graphicsParser, { layers })
}

function * loadGraphicsAttributeGraphicsFlow () {
  const graphics = yield call(getGraphicsAttributeGraphics)
  return yield call(graphicsParser, { attributeGraphics: graphics })
}

export function * newElementFlow ({ payload }) {
  const { name, groupName, layerName, geometryType, shorthand } = payload
  const body = { name, groupName, layerName, geometryType, shorthand }
  const { error } = yield newElementEffect({ body })
  if (error) {
    console.warn('couldnt even', error)
    return yield put(graphicsActions.getGraphicsFailure())
  }
  return yield call(loadGraphicsFlow)
}

export function * newAttributeFlow ({ payload }) {
  const { error } = yield newAttributeEffect({ body: payload })
  if (error) {
    console.warn('couldnt even', error)
    return yield put(graphicsActions.getGraphicsFailure())
  }
  return yield call(loadGraphicsFlow)
}

export function * connectAttributeFlow ({ payload }) {
  const result = yield newAttributeEffect({ body: payload })
  if (result.error) {
    console.warn('couldnt even', result.error)
    yield put(graphicsActions.connectAttributeFail(payload))
  }
  yield call(loadGraphicsThemeAttributesFlow)
  return yield put(graphicsActions.connectAttributeSuccess(payload))
}

export function * loadContextDataFlow () {
  const result = yield loadContextDataEffect()
  if (result.error) {
    return yield put(graphicsActions.loadContextDataFailure())
  }
  return yield put(graphicsActions.loadContextDataSuccess(_.mapValues(_.keyBy('id'), result)))
}

export default function * () {
  return yield all([
    takeLatest(graphicsTypes.GET_GRAPHICS, loadGraphicsFlow),
    takeLatest(graphicsTypes.GET_GRAPHICS_THEMES, loadGraphicsThemesFlow),
    takeLatest(graphicsTypes.GET_GRAPHICS_THEME_ATTRIBUTES, loadGraphicsThemeAttributesFlow),
    takeLatest(graphicsTypes.GET_GRAPHICS_ATTRIBUTE_UNITS, loadGraphicsAttributeUnitsFlow),
    takeLatest(graphicsTypes.GET_GRAPHICS_ELEMENTS, loadGraphicsElementsFlow),
    takeLatest(graphicsTypes.GET_GRAPHICS_GROUPS, loadGraphicsGroupsFlow),
    takeLatest(graphicsTypes.GET_GRAPHICS_LAYERS, loadGraphicsLayersFlow),
    takeLatest(graphicsTypes.GET_GRAPHICS_ATTRIBUTE_GRAPHICS, loadGraphicsAttributeGraphicsFlow),
    takeLatest(graphicsTypes.GET_GRAPHICS_ATTRIBUTES, loadGraphicsAttributesFlow),
    takeEvery(graphicsTypes.NEW_ELEMENT, newElementFlow),
    takeEvery(graphicsTypes.NEW_ATTRIBUTE, newAttributeFlow),
    takeEvery(graphicsTypes.CONNECT_ATTRIBUTE, connectAttributeFlow),
    takeLatest(graphicsTypes.LOAD_CONTEXT_DATA, loadContextDataFlow)
  ])
}
